


















import { computed, defineComponent } from "@vue/composition-api";
import {
  Chart,
  ChartConfiguration,
  Tooltip,
  DoughnutController,
  ArcElement,
  Legend,
  TooltipItem,
  UpdateMode,
} from "chart.js";
import { Color } from "chart.js/types/color";
import { $t } from "@/lib/helpers/i18nHelper";
import { numberWithCommas } from "@/lib/helpers/numberHelpers";
import StatisticChart from "@/views/PageDashboard/components/StatisticChart.vue";
import { ToolTipConfig } from "@/views/PageDashboard/config";
import MonthGrowthView from "./MonthGrowthView.vue";

interface RoleStat {
  name: string;
  label: string;
  value: number;
}

export interface UserGrowthStats {
  prevMonth: number;
  currentMonth: number;
}

Chart.register({
  ArcElement,
  DoughnutController,
  Legend,
  Tooltip,
});

export default defineComponent({
  name: "TotalUsersBlock",
  components: { StatisticChart, MonthGrowthView },
  props: {
    data: { type: Object as () => Record<string, number>, required: true },
    userGrowthStats: { type: Object as () => UserGrowthStats, required: true },
  },
  setup(props) {
    const totalUsers = computed(() => {
      return numberWithCommas(
        Object.keys(props.data).reduce((value: number, key: string) => {
          return value + props.data[key];
        }, 0)
      );
    });

    const roleUsers = computed<RoleStat[]>(() => {
      return Object.entries(props.data)
        .map((stat: [string, number]) => {
          return {
            name: stat[0],
            label: $t(stat[0]),
            value: stat[1],
          };
        })
        .sort((a: RoleStat, b: RoleStat) => a.value - b.value);
    });

    const labels = roleUsers.value.map((roleStat: RoleStat) => roleStat.label);
    const data = {
      labels,
      datasets: [
        {
          data: (() => {
            const values = roleUsers.value.map((roleStat: RoleStat) => roleStat.value);
            const total = values.reduce((a, v) => a + v, 0);
            return values.map(v => Math.max((v / total) * 100, 1));
          })(),
          backgroundColor: ["#61a1f6", "#ff8368", "#ef5da8", "#a5a6f6", "#83eac1"],
        },
      ],
    };

    const getOrCreateLegendList = (chart: Chart, id: string) => {
      const legendContainer = document.getElementById(id);
      let listContainer = legendContainer?.querySelector("ul");
      if (!listContainer) {
        listContainer = document.createElement("ul");
        listContainer.style.display = "flex";
        listContainer.style.flexDirection = "column";
        listContainer.style.margin = "0";
        listContainer.style.padding = "0";
        legendContainer?.appendChild(listContainer);
      }
      return listContainer;
    };

    const htmlLegendPlugin = {
      id: "htmlLegend",
      afterUpdate(chart: Chart, args: { mode: UpdateMode }, options: any) {
        const ul = getOrCreateLegendList(chart, options.containerID);
        while (ul?.firstChild) {
          ul.firstChild.remove();
        }

        const items = chart.options.plugins?.legend?.labels?.generateLabels!(chart);
        items?.forEach((item, index) => {
          const li = document.createElement("li");
          li.style.display = "flex";
          li.style.flexDirection = "row";
          li.style.justifyContent = "space-between";
          li.style.marginBottom = "20px";

          const wrapper = document.createElement("div");
          wrapper.style.display = "flex";
          wrapper.style.alignItems = "center";

          const boxSpan = document.createElement("span");
          boxSpan.style.background = `${item.fillStyle}`;
          boxSpan.style.display = "inline-block";
          boxSpan.style.height = "12px";
          boxSpan.style.width = "12px";
          boxSpan.style.borderRadius = "50%";
          boxSpan.style.marginRight = "8px";

          const textSpan = document.createElement("span");
          textSpan.style.fontSize = "12px";
          const text = document.createTextNode(item.text);
          textSpan.appendChild(text);

          const valueSpan = document.createElement("span");
          const value = document.createTextNode(numberWithCommas(roleUsers.value[index].value));
          valueSpan.style.fontSize = "12px";
          valueSpan.appendChild(value);

          li.appendChild(wrapper);
          wrapper.appendChild(boxSpan);
          wrapper.appendChild(textSpan);
          li.appendChild(valueSpan);
          ul.appendChild(li);
        });
      },
    };

    const chartConfig = computed<ChartConfiguration>(() => {
      return {
        type: "doughnut",
        data,
        options: {
          borderWidth: 0,
          cutout: "70%",
          responsive: true,
          plugins: {
            htmlLegend: {
              containerID: "statistic-total-users-legend",
            },
            tooltip: {
              ...ToolTipConfig,
              callbacks: {
                label: (tooltipItem: TooltipItem<any>) => {
                  const value = roleUsers.value[tooltipItem.dataIndex].value;
                  const label = data.labels![tooltipItem.dataIndex];
                  return `${label}: ${value}`;
                },
              },
            },
            legend: {
              display: false,
              position: "left",
              labels: {
                usePointStyle: true,
                generateLabels: chart => {
                  const data = chart.data;
                  return data.labels!.map((label, i) => {
                    const meta = chart.getDatasetMeta(0);
                    const style = meta.controller.getStyle(i, false);

                    return {
                      text: `${label}s`,
                      fillStyle: style.backgroundColor as Color,
                      strokeStyle: style.borderColor as Color,
                      lineWidth: style.borderWidth as number,
                      padding: 50,
                    };
                  });
                },
              },
            },
          },
        },
        plugins: [htmlLegendPlugin],
      };
    });

    return {
      roleUsers,
      totalUsers,
      chartConfig,
    };
  },
});
